import React from 'react';

import { Modal, ModalHeader, ModalBody, ModalFooter } from 'Components/Modal';
import { PrimaryButton, SecondaryButton } from 'Components/Button';

import WaitlistApi from 'Services/Api/Admin/Waitlist';

const STATUS_LABELS = {
    confirmed_in_person: 'Confirmed In-Person',
    external_waitlist:   'External Waitlist',
    internal_waitlist:   'Internal Waitlist',
    virtual:             'Virtual',
    cancelled:           'Cancelled',
};

/**
 * Lets an admin mark a delegate as attending in person or not attending (virtual).
 *
 * Goes through the waitlist endpoint rather than the user update so the capacity guard,
 * group sync and attendance audit log all still apply, exactly as they do when a delegate
 * cancels their own place.
 *
 * @param {object} user             The delegate being changed.
 * @param {object|null} capacity    Capacity block from the waitlist api, used to disable
 *                                  the in-person option once the room is full.
 * @param {function} [onUpdated]    Called with the new attendance_status once the api confirms.
 * @param {function} onClose        Injected by the modal container.
 */
export default class AttendanceStatusModal extends React.Component {
    state = {
        working: null,
        error: null,
    };

    setStatus = async (status) => {
        const { user, onUpdated, onClose } = this.props;

        this.setState({ working: status, error: null });

        const response = await WaitlistApi.updateUserStatus(user.id, {
            attendance_status: status,
        });

        if (response?.success) {
            onUpdated?.(status);
            onClose();

            return;
        }

        this.setState({
            working: null,
            error: response?.message ?? 'Something went wrong. Please try again.',
        });
    };

    handleAttending = () => this.setStatus('confirmed_in_person');

    handleNotAttending = () => this.setStatus('virtual');

    render () {
        const { user, capacity, onClose } = this.props;
        const { working, error } = this.state;

        const currentStatus = user?.attendance_status;
        const alreadyInPerson = currentStatus === 'confirmed_in_person';

        // Delegates on an executive held place sit outside the general allocation, so a
        // full room does not apply to them — matching the api, which lets them through.
        const isExecutive = (user?.groups ?? []).some((group) => group?.name === 'executives');

        // Someone already holding a seat is not taking another one, so a full room only
        // blocks delegates who would be new to it. The api refuses either way — this just
        // stops the click before it fails.
        const inPersonFull = capacity?.is_full === true && !alreadyInPerson && !isExecutive;

        return (
            <Modal size="max-w-lg">
                <ModalHeader onClose={ onClose }>
                    Update Attendance
                </ModalHeader>

                <ModalBody>
                    <div className="space-y-6">
                        <p className="text-sm text-gray-700 text-center">
                            <strong>{ user?.first_name } { user?.last_name }</strong> — { user?.email }
                            <br />
                            Current status: { STATUS_LABELS[currentStatus] ?? currentStatus ?? 'Unknown' }
                        </p>

                        <div className="space-y-2 text-center">
                            <p className="text-gray-700 text-base font-medium">Attending in person</p>
                            <p className="text-gray-700 text-sm">
                                Confirms a seat at the venue and moves them onto the in-person group.
                            </p>
                            <PrimaryButton
                                onClick={ this.handleAttending }
                                text={ alreadyInPerson ? 'Already attending in person' : 'Mark as attending' }
                                working={ working === 'confirmed_in_person' }
                                disabled={ inPersonFull || alreadyInPerson || working !== null }
                            />
                            { inPersonFull && (
                                <p className="text-red-500 text-sm">
                                    In-person capacity is full ({ capacity.seats_held } of { capacity.in_person_capacity } seats
                                    taken, including { capacity.outstanding_offers } outstanding waitlist offer(s)).
                                    Free a seat before confirming another delegate.
                                </p>
                            ) }
                        </div>

                        <div className="space-y-2 text-center border-t border-gray-100 pt-6">
                            <p className="text-gray-700 text-base font-medium">Not attending in person</p>
                            <p className="text-gray-700 text-sm">
                                Moves them to virtual. If they were holding a seat it is released and
                                offered to the next delegate on the waitlist.
                            </p>
                            <PrimaryButton
                                onClick={ this.handleNotAttending }
                                text={ currentStatus === 'virtual' ? 'Already virtual' : 'Mark as not attending' }
                                working={ working === 'virtual' }
                                disabled={ currentStatus === 'virtual' || working !== null }
                            />
                        </div>

                        { error && (
                            <p className="text-red-500 text-sm text-center">{ error }</p>
                        ) }
                    </div>
                </ModalBody>

                <ModalFooter alignment="center">
                    <SecondaryButton
                        onClick={ onClose }
                        text="Close"
                    />
                </ModalFooter>
            </Modal>
        );
    }
}
